import classnames from "classnames/bind";
import { useTranslation } from "next-i18next";
import React, { useEffect, useMemo, useState } from "react";

import { KINDS, SIZES } from "@/components/buttons/buttons.constants";
import LinkButton from "@/components/buttons/link-button";

import { useIsArcade } from "@/hooks/useIsArcade";

import CrumbSeparator from "../../assets/icons/crumb-separator.svg";
import { BreadcrumbLinkProps, PAGE_TYPE, PAGE_TYPES } from "./breadcrumbs.constants";
import styles from "./breadcrumbs.module.scss";

const cx = classnames.bind(styles);

const linkButtonProps = {
    disabled: false,
    fluid: false,
    kind: KINDS.UNSET,
    size: SIZES.UNSET,
    newTab: false,
    show: true,
};

interface Props {
    pageType: PAGE_TYPE;
    parentLink: BreadcrumbLinkProps;
    currentLink: BreadcrumbLinkProps;
}
export const BreadCrumbs: React.FC<Props> = ({ currentLink, parentLink, pageType }) => {
    const { t } = useTranslation();
    const isArcade = useIsArcade();

    const rootBreadCrumb = useMemo(() => {
        let rootTitle = "",
            rootSlug = "";

        if (pageType === PAGE_TYPES.CATEGORY || pageType === PAGE_TYPES.IN_STORE) {
            rootTitle = t("breadcrumbs.allCategories");
            rootSlug = "categories";
        } else if (pageType === PAGE_TYPES.GIFT_CARD) {
            rootTitle = t("breadcrumbs.allGiftCards");
            rootSlug = "gift-cards";
        }

        return {
            id: null,
            name: rootTitle,
            parentId: null,
            description: "",
            rootPageUrl: `/${rootSlug}`,
        };
    }, [pageType, t]);

    const [breadCrumbs, setBreadCrumbs] = useState<Array<BreadcrumbLinkProps>>([rootBreadCrumb]);

    const breadCrumbsLastIndex = breadCrumbs.length - 1;

    useEffect(() => {
        if (parentLink) {
            setBreadCrumbs([rootBreadCrumb, parentLink, currentLink]);
        } else {
            setBreadCrumbs([rootBreadCrumb, currentLink]);
        }
    }, [rootBreadCrumb, currentLink, parentLink]);

    const breadCrumbsContainerClassName = cx({
        [styles.breadCrumbsContainer]: true,
        [styles.breadCrumbsContainerArcade]: isArcade,
    });

    return (
        <div className={breadCrumbsContainerClassName}>
            {breadCrumbs.map(({ id, name, rootPageUrl }, index) => (
                <span key={id}>
                    <LinkButton {...linkButtonProps} label={name} url={rootPageUrl} />
                    {name && breadCrumbsLastIndex !== index ? <CrumbSeparator /> : null}
                </span>
            ))}
        </div>
    );
};
